import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Package, AlertTriangle, Plus, Minus, Search,
  Trash2, Loader2, History, Edit2, Save
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StockPage() {
  const { token } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inventory');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', unit: 'kg', current_stock: 0, min_stock: 5, cost_per_unit: 0 });
  const [editId, setEditId] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    try {
      const [ingRes, alertRes, logRes] = await Promise.all([
        fetch(`${API}/stock/ingredients`, { headers }),
        fetch(`${API}/stock/alerts`, { headers }),
        fetch(`${API}/stock/logs?limit=30`, { headers }),
      ]);
      if (ingRes.ok) setIngredients(await ingRes.json());
      if (alertRes.ok) setAlerts(await alertRes.json());
      if (logRes.ok) setLogs(await logRes.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [token]);

  const addIngredient = async () => {
    if (!newItem.name) { toast.error('Nom requis'); return; }
    const res = await fetch(`${API}/stock/ingredients`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify(newItem),
    });
    if (res.ok) {
      toast.success('Ingredient ajoute');
      setShowAdd(false);
      setNewItem({ name: '', unit: 'kg', current_stock: 0, min_stock: 5, cost_per_unit: 0 });
      fetchData();
    }
  };

  const deleteIngredient = async (id) => {
    if (!window.confirm('Supprimer cet ingredient ?')) return;
    await fetch(`${API}/stock/ingredients/${id}`, { method: 'DELETE', headers });
    toast.success('Supprime');
    fetchData();
  };

  const adjustStock = async (ingredientId) => {
    const qty = parseFloat(adjustQty);
    if (isNaN(qty) || qty === 0) { toast.error('Quantite invalide'); return; }
    await fetch(`${API}/stock/adjust`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ingredient_id: ingredientId, quantity: qty, reason: 'Ajustement manuel' }),
    });
    toast.success('Stock ajuste');
    setAdjusting(null);
    setAdjustQty('');
    fetchData();
  };

  const filtered = ingredients.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));

  if (loading) return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="stock-page">
      <header className="border-b border-[#1E1E1E] sticky top-0 z-40 bg-[#0C0C0C]/95 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[#666] hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <Package className="w-5 h-5 text-[#D4A853]" />
            <span className="font-bold text-lg">Gestion des stocks</span>
          </div>
          {alerts.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium" data-testid="stock-alert-badge">
              <AlertTriangle className="w-3.5 h-3.5" /> {alerts.length} alerte{alerts.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <div className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-2xl p-4 mb-6" data-testid="stock-alerts">
            <h3 className="text-sm font-bold text-[#EF4444] mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Stock bas
            </h3>
            <div className="space-y-1">
              {alerts.map(a => (
                <div key={a.id} className="text-xs text-[#CCC]">
                  <span className="font-medium text-white">{a.name}</span> : {a.current_stock} {a.unit} (min: {a.min_stock})
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs + Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-[#1E1E1E]">
            {[
              { key: 'inventory', label: 'Inventaire', icon: Package },
              { key: 'history', label: 'Historique', icon: History },
            ].map(tb => (
              <button key={tb.key} onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === tb.key ? 'bg-[#D4A853] text-black' : 'text-[#888] hover:text-white'
                }`} data-testid={`stock-tab-${tb.key}`}>
                <tb.icon className="w-4 h-4" /> {tb.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 bg-[#111] border border-[#1E1E1E] rounded-xl text-sm text-white placeholder:text-[#555] focus:outline-none w-48" />
            </div>
            <Button onClick={() => setShowAdd(true)} className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold text-sm h-9" data-testid="add-ingredient">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        {/* Add Modal */}
        {showAdd && (
          <div className="bg-[#111] border border-[#D4A853]/30 rounded-2xl p-5 mb-6" data-testid="add-ingredient-form">
            <h3 className="font-bold text-sm mb-3">Nouvel ingredient</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Input value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})}
                placeholder="Nom" className="bg-[#0C0C0C] border-[#333] text-white" data-testid="ing-name" />
              <Input value={newItem.unit} onChange={e => setNewItem({...newItem, unit: e.target.value})}
                placeholder="Unite" className="bg-[#0C0C0C] border-[#333] text-white" data-testid="ing-unit" />
              <Input type="number" value={newItem.current_stock} onChange={e => setNewItem({...newItem, current_stock: parseFloat(e.target.value) || 0})}
                placeholder="Stock" className="bg-[#0C0C0C] border-[#333] text-white" />
              <Input type="number" value={newItem.min_stock} onChange={e => setNewItem({...newItem, min_stock: parseFloat(e.target.value) || 0})}
                placeholder="Stock min" className="bg-[#0C0C0C] border-[#333] text-white" />
              <Input type="number" value={newItem.cost_per_unit} onChange={e => setNewItem({...newItem, cost_per_unit: parseFloat(e.target.value) || 0})}
                placeholder="Cout/unite" className="bg-[#0C0C0C] border-[#333] text-white" />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={addIngredient} className="bg-[#2D6A4F] hover:bg-[#1B5A3F] text-white text-sm" data-testid="save-ingredient">
                <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
              </Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-[#888] text-sm">Annuler</Button>
            </div>
          </div>
        )}

        {/* Inventory Table */}
        {tab === 'inventory' && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden" data-testid="inventory-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E1E1E]">
                  <th className="text-left text-xs text-[#888] px-5 py-3">Ingredient</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Stock</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Min</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Cout/u</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Statut</th>
                  <th className="text-right text-xs text-[#888] px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-[#666] text-sm">
                    {ingredients.length === 0 ? 'Aucun ingredient. Cliquez "Ajouter" pour commencer.' : 'Aucun resultat'}
                  </td></tr>
                ) : filtered.map(ing => (
                  <tr key={ing.id} className="border-b border-[#1E1E1E]/50 hover:bg-[#1A1A1A]" data-testid={`ing-row-${ing.id}`}>
                    <td className="px-5 py-3 text-sm font-medium text-white">{ing.name}</td>
                    <td className="px-5 py-3 text-sm text-[#CCC]">
                      {adjusting === ing.id ? (
                        <div className="flex items-center gap-1">
                          <Input type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)}
                            className="w-20 h-7 text-xs bg-[#0C0C0C] border-[#333] text-white" placeholder="+/-" />
                          <button onClick={() => adjustStock(ing.id)} className="text-[#2D6A4F] hover:text-white"><Save className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setAdjusting(null)} className="text-[#666] hover:text-white text-xs">x</button>
                        </div>
                      ) : (
                        <span>{ing.current_stock} {ing.unit}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-[#888]">{ing.min_stock} {ing.unit}</td>
                    <td className="px-5 py-3 text-sm text-[#888]">{ing.cost_per_unit} THB</td>
                    <td className="px-5 py-3">
                      {ing.current_stock <= ing.min_stock ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EF4444]/15 text-[#EF4444]">BAS</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2D6A4F]/15 text-[#2D6A4F]">OK</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setAdjusting(ing.id); setAdjustQty(''); }}
                          className="p-1.5 rounded-lg hover:bg-[#222] text-[#888] hover:text-[#D4A853]" title="Ajuster">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteIngredient(ing.id)}
                          className="p-1.5 rounded-lg hover:bg-[#222] text-[#888] hover:text-[#EF4444]" title="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* History */}
        {tab === 'history' && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden" data-testid="stock-history">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E1E1E]">
                  <th className="text-left text-xs text-[#888] px-5 py-3">Date</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Ingredient</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Quantite</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Nouveau stock</th>
                  <th className="text-left text-xs text-[#888] px-5 py-3">Raison</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-[#666] text-sm">Aucun historique</td></tr>
                ) : logs.map((log, i) => (
                  <tr key={i} className="border-b border-[#1E1E1E]/50">
                    <td className="px-5 py-3 text-xs text-[#888]">{new Date(log.created_at).toLocaleString('fr-FR')}</td>
                    <td className="px-5 py-3 text-sm text-white">{log.ingredient_name}</td>
                    <td className="px-5 py-3 text-sm">
                      <span className={log.quantity > 0 ? 'text-[#2D6A4F]' : 'text-[#EF4444]'}>
                        {log.quantity > 0 ? '+' : ''}{log.quantity}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-[#CCC]">{log.new_stock}</td>
                    <td className="px-5 py-3 text-xs text-[#888]">{log.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
